import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _, { forEach, has, head, includes, isEmpty, reject } from 'lodash';
import DiscoverActions from '../../common/redux/actions/DiscoverActions';
import { useNavigateProtected } from '../../common/utilities/with-router';
import { useHasValueChanged } from '../../common/utilities/state-helpers.hook';
import { IViz } from '../interfaces';
import { skipToken, useSuspenseQuery } from '@apollo/client';
import { DatasetQueries, DiscoverQueries } from '../../common/graphql';
import { ACCOUNT_SELECTORS } from '../../common/redux/selectors/AccountSelectors';
import { VIZ_SELECTORS } from '../../common/redux/selectors/viz-selectors';
import { IDataset } from '../../datasets';
import { DO_NOT_TRACK } from '../../common/Constants';

export const usePinnedDiscoveries = ({
  loadPinnedDiscoveries,
}: {
  loadPinnedDiscoveries: boolean;
}) => {
  const navigate = useNavigateProtected();
  const dispatch = useDispatch();
  const [pinnedVisualizations, setPinnedVisualizations] = useState<IViz[]>([]);

  // @IMPORTANT This datasets call is important for rehydrating all Viz
  const { data: { datasets = [] } = {} } = useSuspenseQuery<{
    datasets: IDataset[];
  }>(
    DatasetQueries.DatasetQuery,
    loadPinnedDiscoveries ? { fetchPolicy: 'cache-first' } : skipToken,
  );

  const { data: { visualizations = [] } = {} } = useSuspenseQuery<{
    visualizations: IViz[];
  }>(
    DiscoverQueries.DiscoveriesQuery,
    loadPinnedDiscoveries ? undefined : skipToken,
  );

  const pinnedDiscoveryIds = useSelector(state =>
    ACCOUNT_SELECTORS.getPinnedDiscoveries(state),
  );

  const openDiscoveries = useSelector(state =>
    VIZ_SELECTORS.getOpenDiscoveries(state),
  );

  // don't trigger changes based on openDiscoveries changing
  const ignoredOpenDiscoDependency = useRef(openDiscoveries);
  ignoredOpenDiscoDependency.current = openDiscoveries;

  useEffect(() => {
    if (isEmpty(datasets)) {
      return;
    }

    // copied from PinnedVizQuery
    const sortOrders = {};

    // remove opened pins
    const pinsToOpen = reject(pinnedDiscoveryIds, id =>
      includes(ignoredOpenDiscoDependency.current ?? [], id),
    );

    const _visualizations = _(visualizations as IViz[])
      .filter(v => {
        const index = pinsToOpen.findIndex(id => id === v.id);
        // track the position of the pinned id so we can be sure to put them back in the correct order
        if (index !== -1) {
          sortOrders[v.id] = index;
        }
        return index !== -1;
      })
      .value();

    setPinnedVisualizations(_.sortBy(_visualizations, v => sortOrders[v.id]));
  }, [pinnedDiscoveryIds, datasets, visualizations]);

  // loads pinned discoveries based on listDiscoveries call
  useEffect(() => {
    if (loadPinnedDiscoveries && !isEmpty(pinnedVisualizations)) {
      forEach(pinnedVisualizations, viz => {
        if (!has(openDiscoveries, viz?.id)) {
          // Not tracked: reopening the user's pins on startup is not a choice to open them.
          dispatch(DiscoverActions.openDiscoveryTracked({ viz }, DO_NOT_TRACK));
        }
      });
    }
  }, [
    dispatch,
    navigate,
    pinnedVisualizations,
    openDiscoveries,
    loadPinnedDiscoveries,
  ]);

  const numPinsToLoad = useMemo(
    () =>
      reject(pinnedVisualizations, ({ id: vizId }) =>
        has(openDiscoveries, vizId),
      )?.length,
    [openDiscoveries, pinnedVisualizations],
  );

  const hasPinsChanged = useHasValueChanged({
    value: numPinsToLoad,
    defaultValue: 0,
  });

  const pinLoadsComplete = hasPinsChanged && numPinsToLoad === 0;

  useEffect(() => {
    const vizId = head(pinnedVisualizations)?.id;
    // setDisplayDiscovery was called opening other visualizations, override here
    if (pinLoadsComplete && has(openDiscoveries, vizId)) {
      dispatch(DiscoverActions.setDisplayDiscovery(vizId));
    }
  }, [pinnedVisualizations, pinLoadsComplete, dispatch, openDiscoveries]);
};
